import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { GlobeHero } from "../components/globe/GlobeHero";
import { BridgeDiagram } from "../components/BridgeDiagram";
import { Reveal, Overline } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { CTASection } from "../components/CTASection";
import { AudienceTag } from "../components/Tags";

const PILLARS = [
  {
    n: "01",
    title: "Bilingual Professional Expertise",
    body: "Native-level fluency in both English and Japanese. Every engagement is handled by a professional person — never outsourced, never automated.",
  },
  {
    n: "02",
    title: "Cultural Understanding",
    body: "We bridge cultural gaps, not just language gaps — the pauses, the protocol, the meaning behind the words.",
  },
  {
    n: "03",
    title: "Present Until the Outcome",
    body: "We stay in the room until the agreement is real — through negotiation, signature, and the first ninety days after.",
  },
  {
    n: "04",
    title: "Both Directions, One Bridge",
    body: "Rooted in Tokyo and Toronto, we work Canada to Japan and Japan to Canada with equal fluency and equal care.",
  },
];

const PRACTICES = [
  {
    to: "/services",
    tags: ["business", "individual"],
    title: "Interpretation Services",
    body: "Simultaneous, consecutive, on-site negotiation support, and board-level interpretation.",
    testId: "practice-interpretation",
  },
  {
    to: "/services",
    tags: ["business", "individual"],
    title: "Cultural Consulting",
    body: "Cross-cultural strategy, market entry, expatriate integration, and organizational alignment.",
    testId: "practice-consulting",
  },
  {
    to: "/education-academics",
    tags: ["business"],
    title: "Private Sessions",
    body: "Executive workshops for cross-border M&A, joint ventures, and relocation — delivered on-site.",
    testId: "practice-education",
  },
  {
    to: "/store",
    tags: ["individual"],
    title: "Guides & Templates",
    body: "Working holiday guides, workplace English, and cultural playbooks in our digital store.",
    testId: "practice-store",
  },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <GlobeHero />
      <BridgeDiagram />

      {/* Editorial intro */}
      <section className="bg-dawn relative overflow-hidden">
        <div className="relative max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
            <div className="lg:col-span-7">
              <Reveal>
                <Overline>THE BRIDGE — 架け橋</Overline>
                <h2 className="mt-6 font-serif text-3xl sm:text-4xl lg:text-5xl leading-[1.15] tracking-tight text-navy">
                  We stand between two business cultures so our clients never have to choose between them.
                </h2>
              </Reveal>
              <Reveal delay={0.12}>
                <p className="mt-8 text-base sm:text-lg leading-relaxed text-charcoal/75 max-w-xl">
                  Ruten Borealis — through Kakehashi, its Japan–Canada platform — is a business bridge.
                  We interpret in the room, advise behind it, and remain accountable after it.
                  Quiet work, done exactly, in both directions.
                </p>
                <Link
                  to="/about"
                  data-testid="intro-about-link"
                  className="group mt-8 inline-flex items-center gap-2 text-sm font-medium text-coral-dark hover:text-coral transition-colors duration-300"
                >
                  Our story
                  <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1" />
                </Link>
              </Reveal>
            </div>
            <div className="lg:col-span-5 relative">
              <Reveal delay={0.15} className="relative">
                <div className="overflow-hidden rounded-sm border border-sand">
                  <img
                    src="/images/tatami-wood.jpg"
                    alt="Wood grain and tatami weave textures"
                    data-testid="intro-tatami-image"
                    className="w-full h-64 object-cover transition-transform duration-700 hover:scale-[1.03]"
                  />
                </div>
                <div className="overflow-hidden rounded-sm border border-sand -mt-10 ml-auto w-3/4 shadow-xl relative z-10">
                  <img
                    src="https://images.pexels.com/photos/16669433/pexels-photo-16669433.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                    alt="Toronto skyline"
                    data-testid="intro-toronto-image"
                    className="w-full h-48 object-cover transition-transform duration-700 hover:scale-[1.03]"
                  />
                </div>
                <p className="mt-4 text-xs font-mono tracking-wider text-charcoal/50 text-right">
                  TOKYO 35.68°N ⇄ TORONTO 43.65°N
                </p>
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* Why Kakehashi */}
      <section data-testid="why-kakehashi" className="bg-cream-surface border-y border-sand">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32">
          <Reveal>
            <Overline>WHY KAKEHASHI</Overline>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-navy max-w-2xl">
              Four quiet commitments, kept in both languages.
            </h2>
          </Reveal>
          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-x-16 gap-y-14">
            {PILLARS.map((p, i) => (
              <Reveal key={p.n} delay={i * 0.08}>
                <div data-testid={`pillar-${p.n}`} className="border-t-2 border-navy pt-6">
                  <span className="font-mono text-sm text-coral-dark tracking-widest">{p.n}</span>
                  <h3 className="mt-3 font-serif text-2xl text-navy">{p.title}</h3>
                  <p className="mt-3 text-sm sm:text-base leading-relaxed text-charcoal/70 max-w-md">{p.body}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <Marquee />

      {/* Practice areas */}
      <section className="bg-cream">
        <div className="max-w-7xl mx-auto px-6 sm:px-12 lg:px-20 py-24 sm:py-32">
          <Reveal>
            <Overline>WHAT WE DO</Overline>
            <h2 className="mt-6 font-serif text-3xl sm:text-4xl tracking-tight text-navy max-w-2xl">
              For organizations crossing the Pacific — and for the people who cross with them.
            </h2>
          </Reveal>
          <div className="mt-14 grid grid-cols-1 sm:grid-cols-2 gap-6">
            {PRACTICES.map((c, i) => (
              <Reveal key={c.testId} delay={i * 0.07}>
                <Link
                  to={c.to}
                  data-testid={c.testId}
                  className="group block bg-cream-surface border border-sand rounded-sm p-8 h-full transition-all duration-300 hover:border-coral/60 hover:-translate-y-1"
                >
                  <div className="flex flex-wrap gap-1.5">
                    {c.tags.map((t) => (
                      <AudienceTag key={t} audience={t} testId={`${c.testId}-tag-${t}`} />
                    ))}
                  </div>
                  <h3 className="mt-5 font-serif text-2xl text-navy group-hover:text-coral-dark transition-colors duration-300">
                    {c.title}
                  </h3>
                  <p className="mt-3 text-sm leading-relaxed text-charcoal/70">{c.body}</p>
                  <span className="mt-6 inline-flex items-center gap-2 text-xs font-mono tracking-widest text-coral-dark">
                    EXPLORE
                    <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1" />
                  </span>
                </Link>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CTASection />
    </div>
  );
}
